import { LitElement, html, type CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController } from '../utils/form-validation.controller.js';
import styles from './styles.js';

export type RadioButtonVariant = 'outlined' | 'filled';

/**
 * uwc-radiobutton
 *
 * Production-grade radio button component with W3C form association and
 * constraint validation. Set `required` to enforce that this radio must
 * be checked.
 *
 * @attr checked  - Whether this radio is selected.
 * @attr value    - Value for this option.
 * @attr name     - Group name — radio buttons with same name form a group.
 * @attr label    - Label text rendered beside the radio.
 * @attr required - This radio must be checked.
 * @attr disabled - Disable the radio button.
 * @attr invalid  - Mark as invalid (red border).
 * @attr variant  - outlined | filled. Default: outlined.
 *
 * @slot default - Custom label content (overrides label attr).
 *
 * @fires uwc-change  - Fired when this radio becomes selected. Detail: { value }.
 * @fires uwc-invalid - Fired when required but unchecked. Detail: { validity, validationMessage }.
 */
export class UwcRadioButton extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = [styles];

  private _validation = new FormValidationController(this);

  // ── Core ─────────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) checked = false;
  @property()                                 value?:  string;
  @property()                                 name?:   string;
  @property()                                 label?:  string;

  // ── Modifiers ─────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid  = false;
  @property({ reflect: true })                variant: RadioButtonVariant = 'outlined';

  override firstUpdated() {
    const input = this.shadowRoot?.querySelector('input') ?? null;
    this._validation.setAnchor(input);
    this._validation.syncFormValue(this.checked ? (this.value ?? 'on') : null);
    this._validation.revalidate(this.checked ? 'on' : '', { required: this.required });
  }

  override updated(changed: Map<PropertyKey, unknown>) {
    super.updated(changed);
    if (changed.has('required')) {
      this._validation.revalidate(this.checked ? 'on' : '', { required: this.required });
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────

  private _onChange(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.checked) return;
    this.checked = true;
    this._validation.handleChange('on', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(this.value ?? 'on');
    this.dispatchEvent(new CustomEvent('uwc-change', {
      bubbles: true, composed: true,
      detail: { value: this.value, originalEvent: e },
    }));
  }

  // ── Render ────────────────────────────────────────────────────────

  override render() {
    const labelContent = this.label
      ? html`<span part="label" class="uwc-rb__label">${this.label}</span>`
      : html`<slot part="label" class="uwc-rb__label"></slot>`;

    return html`
      <label style="display:inline-flex;align-items:center;gap:0.5rem;cursor:${this.disabled ? 'not-allowed' : 'pointer'}">
        <span part="circle" class="uwc-rb">
          <input
            type="radio"
            .checked=${this.checked}
            value=${ifDefined(this.value)}
            name=${ifDefined(this.name)}
            ?disabled=${this.disabled}
            ?required=${this.required}
            aria-required=${this.required ? 'true' : 'false'}
            @change=${this._onChange}
          />
          <span class="uwc-rb__dot"></span>
        </span>
        ${labelContent}
      </label>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-radiobutton': UwcRadioButton; }
}
